// src/components/ProfilePanel.jsx  —  ohapotato v2026.10.36
import React, { useState, useRef } from 'react';
import RecipeCard from './RecipeCard';

const DIETS    = ['Vegetarian','Vegan','Gluten-Free','Dairy-Free','Keto','Paleo','Nut-Free','Halal','Kosher'];
const SKILLS   = ['Beginner','Home Cook','Confident Cook','Chef-Level'];
const PREMIUM_FEATURES = [
  { icon:'📊', title:'Nutrition breakdown', sub:'Full macros, calories, fibre, sodium & % daily values per recipe.', live: true },
  { icon:'🗓️', title:'Weekly meal planner', sub:'7-day AI-generated meal plan with auto shopping list.', live: true },
  { icon:'✦', title:'Unlimited recipe saves', sub:'Free tier is capped at 10 saved recipes.' },
  { icon:'🥔', title:'Pantry IQ Pro', sub:'Set real expiry dates with calendar picker & get push reminders.', live: true },
  { icon:'👨‍🍳', title:'Chef mode', sub:'AI cooking coach: technique breakdowns, flavour science, smart substitutions, plating tips & drink pairings.', live: true },
  { icon:'🌍', title:'Cuisine passport', sub:'Unlock 50+ global cuisine filters and regional technique tips.' },
  { icon:'🤝', title:'Cook with friends', sub:'Share pantry lists and collaborate on recipes in real time.' },
];

export default function ProfilePanel({ user, profile, onUpdateProfile, saved, savedIds, onSave, onRemove, onClose, initialTab = 'saved', onLaunchMealPlanner }) {
  const [tab, setTab]       = useState(initialTab);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savedFlash, setSavedFlash]       = useState(false);
  const [checkoutPrice,   setCheckoutPrice  ] = useState('price_1T96ycRumBUvhlE9XwOxngjt');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError,   setCheckoutError  ] = useState('');
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    displayName:  profile?.displayName  || user.name  || '',
    bio:          profile?.bio          || '',
    skillLevel:   profile?.skillLevel   || 'Home Cook',
    dietaryPrefs: profile?.dietaryPrefs || [],
    avatarUrl:    profile?.avatarUrl    || user.picture || '',
  });
  const [avatarPreview, setAvatarPreview] = useState(form.avatarUrl);

  const handleAvatarChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      setAvatarPreview(ev.target.result);
      setForm(f => ({ ...f, avatarUrl: ev.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const toggleDiet = tag =>
    setForm(f => ({
      ...f,
      dietaryPrefs: f.dietaryPrefs.includes(tag)
        ? f.dietaryPrefs.filter(d => d !== tag)
        : [...f.dietaryPrefs, tag],
    }));

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    await new Promise(r => setTimeout(r, 450));
    onUpdateProfile(form);
    setSavingProfile(false);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2200);
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const res = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId:   checkoutPrice,
          userId:    user.sub,
          userEmail: user.email,
        }),
      });
      if (!res.ok) {
        // Try to get detail from body, fall back to status
        let detail = `Server error ${res.status}`;
        try { const j = await res.json(); detail = j.detail || j.error || detail; } catch {}
        throw new Error(detail);
      }
      const text = await res.text();
      let data = {};
      try { data = JSON.parse(text); } catch { /* non-JSON response */ }
      if (!data.url) throw new Error(data.detail || data.error || 'No redirect URL returned');
      window.location.href = data.url;
    } catch (err) {
      setCheckoutError(err.message || 'Could not start checkout — please try again.');
      setCheckoutLoading(false);
    }
  };

  const displayAvatar = avatarPreview || user.picture;
  const initial = (form.displayName || user.email || '?')[0].toUpperCase();

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.panel} onClick={e => e.stopPropagation()}>

        {/* ── Profile header ───────────────── */}
        <div style={s.header}>
          <div style={s.avatarWrap}>
            {displayAvatar
              ? <img src={displayAvatar} alt="" style={s.avatar} />
              : <div style={s.avatarFallback}>{initial}</div>
            }
            {tab === 'profile' && (
              <>
                <button style={s.avatarCameraBtn} onClick={() => fileRef.current?.click()} title="Change photo">
                  📷
                </button>
                <input ref={fileRef} type="file" accept="image/*"
                  style={{ display:'none' }} onChange={handleAvatarChange} />
              </>
            )}
          </div>
          <div style={s.headerMeta}>
            <h2 style={s.headerName}>{form.displayName || user.email}</h2>
            <p style={s.headerEmail}>{user.email}</p>
            <div style={s.headerBadges}>
              <span style={s.skillBadge}>{form.skillLevel}</span>
            </div>
          </div>
          <button style={s.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* ── Tabs ─────────────────────────── */}
        <div style={s.tabBar}>
          {[
            { id:'saved',   label:`Saved${saved.length ? ` (${saved.length})` : ''}` },
            { id:'profile', label:'Profile' },
          ].map(t => (
            <button key={t.id}
              style={{ ...s.tabBtn, ...(tab === t.id ? s.tabBtnActive : {}) }}
              onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Body ─────────────────────────── */}
        <div style={s.body}>

          {/* SAVED RECIPES */}
          {tab === 'saved' && (saved.length === 0
            ? <div style={s.empty}>
                <div style={s.emptyIcon}>🥔</div>
                <h3 style={s.emptyTitle}>Nothing saved yet</h3>
                <p style={s.emptyText}>Find a recipe you love and tap "Save recipe" to keep it here.</p>
              </div>
            : <div style={s.list}>
                {saved.map((recipe, i) => (
                  <div key={recipe.id}>
                    <RecipeCard recipe={recipe} index={i} user={user} savedIds={savedIds} onSave={onSave} />
                    <button style={s.removeBtn} onClick={() => onRemove(recipe.id)}>Remove</button>
                  </div>
                ))}
              </div>
          )}

          {/* EDIT PROFILE */}
          {tab === 'profile' && (
            <div style={s.form}>
              <p style={s.formHint}>Tap your photo above to change it.</p>

              <div style={s.field}>
                <label style={s.label}>Display name</label>
                <input style={s.input} value={form.displayName} maxLength={40}
                  onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
                  placeholder="Your name" />
              </div>

              <div style={s.field}>
                <label style={s.label}>Bio <span style={s.optional}>(optional)</span></label>
                <textarea style={{ ...s.input, ...s.textarea }} value={form.bio} rows={3} maxLength={120}
                  onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                  placeholder="A little about how you cook…" />
                <span style={s.charCount}>{form.bio.length}/120</span>
              </div>

              <div style={s.field}>
                <label style={s.label}>Skill level</label>
                <div style={s.pillRow}>
                  {SKILLS.map(skill => (
                    <button key={skill}
                      style={{ ...s.pill, ...(form.skillLevel === skill ? s.pillActive : {}) }}
                      onClick={() => setForm(f => ({ ...f, skillLevel: skill }))}>
                      {skill}
                    </button>
                  ))}
                </div>
              </div>

              <div style={s.field}>
                <label style={s.label}>Dietary preferences</label>
                <p style={s.fieldHint}>Recipes will be prioritised to match your diet.</p>
                <div style={s.pillRow}>
                  {DIETS.map(diet => (
                    <button key={diet}
                      style={{ ...s.pill, ...(form.dietaryPrefs.includes(diet) ? s.pillGreen : {}) }}
                      onClick={() => toggleDiet(diet)}>
                      {form.dietaryPrefs.includes(diet) ? '✓ ' : ''}{diet}
                    </button>
                  ))}
                </div>
              </div>

              <button style={{ ...s.saveBtn, ...(savingProfile ? s.saveBtnLoading : {}), ...(savedFlash ? s.saveBtnDone : {}) }}
                onClick={handleSaveProfile} disabled={savingProfile}>
                {savingProfile ? 'Saving…' : savedFlash ? '✓ Saved!' : 'Save changes'}
              </button>
            </div>
          )}

          {/* PREMIUM */}
          {tab === 'premium' && (
            <div style={s.premiumTab}>
              <div style={s.premiumHero}>
                <div style={s.premiumEmoji}>✦</div>
                <h3 style={s.premiumTitle}>OhAPotato Premium</h3>
                <p style={s.premiumTagline}>Everything a serious home cook needs, in one place.</p>
                <div style={s.premiumPricing}>
                  <span style={s.premiumPrice}>$4.99</span>
                  <span style={s.premiumPer}> / month</span>
                  <span style={s.premiumAlt}> · or $39 / year</span>
                </div>
              </div>

              <div style={s.featureList}>
                {PREMIUM_FEATURES.map((f, i) => (
                  <div key={i} style={s.featureItem}>
                    <span style={s.featureIcon}>{f.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                        <p style={s.featureTitle}>{f.title}</p>
                        {f.live && <span style={s.liveBadge}>✓ Live</span>}
                      </div>
                      <p style={s.featureSub}>{f.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {false ? (
                <>
                  <div style={s.alreadyPremium}>✓ You're on Premium — thank you! 🥔</div>
                  <button style={s.mealPlanBtn} onClick={() => { onClose(); onLaunchMealPlanner?.(); }}>
                    🗓️ Open Weekly Meal Planner →
                  </button>
                </>
              ) : (
                <div style={s.upgradeBox}>
                  <div style={s.pricingRow}>
                    <div style={{...s.pricingCard, ...(checkoutPrice === 'price_1T96ycRumBUvhlE9XwOxngjt' ? s.pricingCardActive : {})}}
                      onClick={() => setCheckoutPrice('price_1T96ycRumBUvhlE9XwOxngjt')}>
                      <div style={s.pricingAmount}>$4.99</div>
                      <div style={s.pricingPeriod}>per month</div>
                    </div>
                    <div style={{...s.pricingCard, ...(checkoutPrice === 'price_1T96ycRumBUvhlE93SlDDHTi' ? s.pricingCardActive : {})}}
                      onClick={() => setCheckoutPrice('price_1T96ycRumBUvhlE93SlDDHTi')}>
                      <div style={s.pricingBestValue}>Best value</div>
                      <div style={s.pricingAmount}>$39</div>
                      <div style={s.pricingPeriod}>per year</div>
                      <div style={s.pricingSave}>Save 35%</div>
                    </div>
                  </div>

                  {checkoutError && (
                    <div style={s.checkoutError}>{checkoutError}</div>
                  )}

                  <button
                    style={{...s.checkoutBtn, ...(checkoutLoading ? s.checkoutBtnLoading : {})}}
                    onClick={handleCheckout}
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading ? 'Redirecting to Stripe…' : '✦ Upgrade to Premium →'}
                  </button>
                </div>
              )}
              <p style={s.premiumLegal}>Cancel any time · Stripe-secured checkout · 7-day free trial</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position:'fixed', inset:0, background:'rgba(0,0,0,0.42)',
    zIndex:300, display:'flex', justifyContent:'flex-end',
    backdropFilter:'blur(4px)', animation:'fadeUp 0.18s ease',
  },
  panel: {
    width:'100%', maxWidth:'460px', height:'100%',
    background:'var(--bg-surface)',
    display:'flex', flexDirection:'column',
    boxShadow:'var(--shadow-lg)',
    animation:'slideIn 0.24s ease',
    overflow:'hidden',
  },

  /* header */
  header: {
    display:'flex', alignItems:'center', gap:'14px',
    padding:'20px 22px', borderBottom:'1px solid var(--border-base)',
    flexShrink:0,
  },
  avatarWrap: { position:'relative', flexShrink:0 },
  avatar: { width:'52px', height:'52px', borderRadius:'50%', objectFit:'cover', display:'block' },
  avatarFallback: {
    width:'52px', height:'52px', borderRadius:'50%',
    background:'var(--accent-orange)', color:'#fff',
    display:'flex', alignItems:'center', justifyContent:'center',
    fontFamily:'var(--font-display)', fontSize:'22px',
  },
  avatarCameraBtn: {
    position:'absolute', bottom:'-2px', right:'-2px',
    width:'22px', height:'22px', borderRadius:'50%',
    background:'var(--bg-surface)', border:'1.5px solid var(--border-base)',
    fontSize:'11px', cursor:'pointer',
    display:'flex', alignItems:'center', justifyContent:'center',
  },
  headerMeta: { flex:1, minWidth:0 },
  headerName: {
    fontFamily:'var(--font-display)', fontSize:'17px',
    color:'var(--text-primary)', marginBottom:'2px',
    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
    transition:'color 0.3s ease',
  },
  headerEmail: { fontSize:'11.5px', color:'var(--text-muted)', marginBottom:'5px' },
  headerBadges: { display:'flex', gap:'5px', flexWrap:'wrap' },
  skillBadge: {
    fontSize:'10px', fontWeight:'600', fontFamily:'var(--font-body)',
    color:'var(--accent-green)', background:'var(--accent-green-bg)',
    padding:'2px 8px', borderRadius:'var(--radius-pill)',
  },
  premiumBadge: {
    fontSize:'10px', fontWeight:'600', fontFamily:'var(--font-body)',
    color:'var(--accent-gold)', background:'var(--accent-gold-bg)',
    padding:'2px 8px', borderRadius:'var(--radius-pill)',
  },
  closeBtn: {
    background:'var(--bg-pill)', border:'none',
    width:'28px', height:'28px', borderRadius:'50%',
    cursor:'pointer', fontSize:'12px', color:'var(--text-muted)',
    display:'flex', alignItems:'center', justifyContent:'center',
    flexShrink:0, fontFamily:'inherit',
  },

  /* tabs */
  tabBar: {
    display:'flex', borderBottom:'1px solid var(--border-base)',
    padding:'0 22px', flexShrink:0,
  },
  tabBtn: {
    padding:'12px 4px', marginRight:'22px',
    border:'none', borderBottom:'2px solid transparent',
    background:'none', cursor:'pointer',
    fontFamily:'var(--font-body)', fontSize:'13px', fontWeight:'500',
    color:'var(--text-muted)',
    transition:'color var(--transition), border-color var(--transition)',
    whiteSpace:'nowrap',
  },
  tabBtnActive: { color:'var(--text-primary)', borderBottomColor:'var(--accent-orange)' },

  /* body */
  body: { flex:1, overflowY:'auto', padding:'20px 22px' },

  /* saved */
  empty: { textAlign:'center', padding:'60px 16px' },
  emptyIcon: { fontSize:'44px', marginBottom:'12px' },
  emptyTitle: { fontFamily:'var(--font-display)', fontSize:'18px', color:'var(--text-primary)', marginBottom:'8px' },
  emptyText: { fontSize:'13.5px', color:'var(--text-muted)', lineHeight:'1.6' },
  list: { display:'flex', flexDirection:'column', gap:'14px' },
  removeBtn: {
    display:'block', marginTop:'4px', marginLeft:'auto',
    background:'none', border:'none', color:'var(--text-muted)',
    fontSize:'11px', cursor:'pointer', textDecoration:'underline',
    fontFamily:'var(--font-body)', padding:'2px 0',
  },

  /* edit form */
  form: { display:'flex', flexDirection:'column', gap:'20px' },
  formHint: { fontSize:'12px', color:'var(--text-muted)', textAlign:'center', marginTop:'-6px' },
  field: { display:'flex', flexDirection:'column', gap:'7px', position:'relative' },
  label: {
    fontSize:'11px', fontWeight:'700', letterSpacing:'0.9px',
    textTransform:'uppercase', color:'var(--text-muted)',
    fontFamily:'var(--font-body)',
  },
  optional: { fontWeight:'400', textTransform:'none', letterSpacing:0, fontSize:'11px' },
  fieldHint: { fontSize:'12px', color:'var(--text-muted)', marginTop:'-3px' },
  input: {
    width:'100%', padding:'10px 12px',
    border:'1.5px solid var(--border-base)', borderRadius:'var(--radius-md)',
    background:'var(--bg-input)', fontFamily:'var(--font-body)',
    fontSize:'13.5px', color:'var(--text-primary)',
    outline:'none', transition:'border-color var(--transition)', resize:'none',
  },
  textarea: { lineHeight:'1.55' },
  charCount: { fontSize:'11px', color:'var(--text-muted)', alignSelf:'flex-end', marginTop:'-4px' },
  pillRow: { display:'flex', gap:'6px', flexWrap:'wrap' },
  pill: {
    padding:'5px 13px',
    border:'1.5px solid var(--border-base)', borderRadius:'var(--radius-pill)',
    background:'transparent', fontFamily:'var(--font-body)',
    fontSize:'12px', fontWeight:'500', color:'var(--text-muted)',
    cursor:'pointer', transition:'all var(--transition)', outline:'none',
  },
  pillActive: { background:'var(--text-primary)', borderColor:'var(--text-primary)', color:'var(--bg-base)' },
  pillGreen:  { background:'var(--accent-green-bg)', borderColor:'var(--accent-green)', color:'var(--accent-green)' },
  saveBtn: {
    padding:'12px', border:'none', borderRadius:'var(--radius-md)',
    background:'var(--accent-orange)', color:'#fff',
    fontFamily:'var(--font-body)', fontSize:'14px', fontWeight:'600',
    cursor:'pointer', transition:'all var(--transition)', marginTop:'4px',
  },
  saveBtnLoading: { opacity:0.6, cursor:'not-allowed' },
  saveBtnDone: { background:'var(--accent-green)' },

  /* premium tab */
  premiumTab: { display:'flex', flexDirection:'column', gap:'0' },
  premiumHero: {
    textAlign:'center', padding:'28px 16px 22px',
    background:'linear-gradient(160deg, var(--accent-gold-bg), transparent)',
    borderRadius:'var(--radius-lg)', marginBottom:'18px',
    border:'1px solid var(--border-base)',
  },
  premiumEmoji: { fontSize:'32px', marginBottom:'10px' },
  premiumTitle: {
    fontFamily:'var(--font-display)', fontSize:'22px',
    color:'var(--text-primary)', marginBottom:'6px',
  },
  premiumTagline: { fontSize:'13.5px', color:'var(--text-muted)', lineHeight:'1.6', marginBottom:'14px' },
  premiumPricing: { display:'flex', alignItems:'baseline', justifyContent:'center', gap:'2px' },
  premiumPrice: { fontSize:'28px', fontWeight:'700', fontFamily:'var(--font-body)', color:'var(--accent-gold)' },
  premiumPer:   { fontSize:'14px', color:'var(--text-muted)' },
  premiumAlt:   { fontSize:'12px', color:'var(--text-muted)' },
  featureList: { display:'flex', flexDirection:'column', gap:'0', marginBottom:'18px' },
  featureItem: {
    display:'flex', gap:'14px', alignItems:'flex-start',
    padding:'13px 4px',
    borderBottom:'1px solid var(--border-base)',
  },
  featureIcon: { fontSize:'20px', flexShrink:0, marginTop:'1px', width:'24px', textAlign:'center' },
  liveBadge: {
    fontSize:'9px', fontWeight:'700', color:'var(--accent-green)',
    background:'var(--accent-green-bg)', padding:'2px 7px',
    borderRadius:'var(--radius-pill)', fontFamily:'var(--font-body)',
    letterSpacing:'0.3px', whiteSpace:'nowrap',
  },
  featureTitle: {
    fontFamily:'var(--font-body)', fontSize:'13.5px', fontWeight:'600',
    color:'var(--text-primary)', marginBottom:'3px',
  },
  featureSub: { fontSize:'12px', color:'var(--text-muted)', lineHeight:'1.5' },
  premiumCTA: {
    display:'block', width:'100%', padding:'13px',
    border:'none', borderRadius:'var(--radius-md)',
    background:'var(--accent-gold)', color:'#fff',
    fontFamily:'var(--font-body)', fontSize:'14px', fontWeight:'700',
    cursor:'pointer', transition:'all var(--transition)',
    letterSpacing:'0.2px', marginBottom:'10px',
  },
  alreadyPremium: {
    textAlign:'center', padding:'14px',
    fontSize:'13.5px', fontWeight:'600',
    color:'var(--accent-green)', background:'var(--accent-green-bg)',
    borderRadius:'var(--radius-md)', marginBottom:'10px',
  },
  /* checkout UI */
  upgradeBox: {
    display:'flex', flexDirection:'column', gap:'12px',
  },
  pricingRow: {
    display:'flex', gap:'10px',
  },
  pricingCard: {
    flex:1, border:'1.5px solid var(--border-base)',
    borderRadius:'var(--radius-md)', padding:'16px 12px',
    textAlign:'center', cursor:'pointer',
    background:'var(--bg-card)', transition:'all 0.15s ease',
    position:'relative',
  },
  pricingCardActive: {
    borderColor:'var(--accent-gold)',
    background:'var(--accent-gold-bg)',
    boxShadow:'0 0 0 3px rgba(201,164,53,0.15)',
  },
  pricingBestValue: {
    position:'absolute', top:'-10px', left:'50%',
    transform:'translateX(-50%)',
    fontSize:'9px', fontWeight:'700', letterSpacing:'0.4px',
    background:'var(--accent-gold)', color:'#fff',
    padding:'2px 8px', borderRadius:'var(--radius-pill)',
    fontFamily:'var(--font-body)', whiteSpace:'nowrap',
  },
  pricingAmount: {
    fontFamily:'var(--font-body)', fontSize:'24px', fontWeight:'800',
    color:'var(--text-primary)', lineHeight:'1', marginBottom:'4px',
  },
  pricingPeriod: {
    fontFamily:'var(--font-body)', fontSize:'11px',
    color:'var(--text-muted)',
  },
  pricingSave: {
    fontFamily:'var(--font-body)', fontSize:'10px', fontWeight:'600',
    color:'var(--accent-green)', marginTop:'4px',
  },
  checkoutBtn: {
    width:'100%', padding:'13px',
    background:'var(--accent-gold)',
    border:'none', borderRadius:'var(--radius-md)',
    color:'#fff', fontFamily:'var(--font-body)',
    fontSize:'14px', fontWeight:'700',
    cursor:'pointer', transition:'opacity 0.2s',
    letterSpacing:'0.2px',
  },
  checkoutBtnLoading: { opacity:0.65, cursor:'not-allowed' },
  checkoutError: {
    fontSize:'12px', color:'#EF4444',
    fontFamily:'var(--font-body)',
    background:'rgba(239,68,68,0.08)',
    border:'1px solid rgba(239,68,68,0.25)',
    borderRadius:'var(--radius-sm)', padding:'8px 10px',
    textAlign:'center',
  },
  premiumLegal: { textAlign:'center', fontSize:'11px', color:'var(--text-muted)', marginTop:'4px', lineHeight:'1.6' },
  mealPlanBtn: {
    display:'block', width:'100%', padding:'12px', marginTop:'10px',
    border:'1.5px solid var(--accent-gold)', borderRadius:'var(--radius-md)',
    background:'transparent', cursor:'pointer',
    fontFamily:'var(--font-body)', fontSize:'14px', fontWeight:'600',
    color:'var(--accent-gold)', transition:'all var(--transition)',
  },
};
